import { useState } from 'react';
import { Card, Badge, Modal, SlidePanel, toast } from '@/components';
import { PanelSection, SpecGrid, SpecCell } from '@/components/SlidePanel';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import styles from './MpCompliance.module.css';

interface Listing {
  id: string; property_id?: string; property_name: string; property_address?: string;
  notification_no: string; license_type: string; days_used: number; max_days: number;
  status: string; notification_date?: string; max_guests?: number;
}
interface ComplianceDoc {
  id: string; listing_id?: string; doc_type: string; doc_name: string; status: string;
  submitted_at: string | null; valid_until: string | null; property_name: string; notification_no?: string;
}
interface ComplianceData { listings: Listing[]; documents: ComplianceDoc[]; }
interface PropertyItem { id: string; name: string; address: string; }

const regulations = [
  { icon: '⚠️', title: '年間180日上限', desc: '住宅宿泊事業法により、民泊の年間営業日数は180日が上限です。超過した場合は行政指導・罰則の対象となります。', color: 'var(--amber)' },
  { icon: '📋', title: '届出・更新義務', desc: '住宅宿泊事業を営むには都道府県知事への届出が必要です。届出番号を取得し、OTAサイトに掲載する義務があります。', color: 'var(--blue)' },
  { icon: '📝', title: '宿泊者名簿の作成', desc: '宿泊者の氏名・住所・国籍等を記載した名簿を作成し、3年間保存する義務があります。外国人の場合はパスポートの写しも必要です。', color: 'var(--green)' },
  { icon: '🏠', title: '管理業者委託（不在時）', desc: '家主不在型の場合は、住宅宿泊管理業者への委託が義務付けられています。管理業者は国土交通大臣の登録が必要です。', color: 'var(--purple)' },
];
const docTypeLabel: Record<string, string> = { periodic_report: '定期報告書', fire_certificate: '消防法適合証明書', insurance: '賠償責任保険証書' };
const docTypeOptions = [
  { value: 'periodic_report', label: '定期報告書' },
  { value: 'fire_certificate', label: '消防法適合証明書' },
  { value: 'insurance', label: '賠償責任保険証書' },
  { value: 'notification', label: '住宅宿泊事業届出書' },
  { value: 'guest_register', label: '宿泊者名簿' },
  { value: 'other', label: 'その他' },
];
const docStatusLabel: Record<string, string> = { pending: '未提出', submitted: '提出済', valid: '有効' };
const docStatusVariant: Record<string, 'gray' | 'blue' | 'green'> = { pending: 'gray', submitted: 'blue', valid: 'green' };

export function MpCompliance() {
  const { data, loading, refetch } = useApi<ComplianceData>('/tenant/minpaku/compliance');
  const { data: properties } = useApi<PropertyItem[]>('/tenant/properties');
  const [showDocs, setShowDocs] = useState(false);
  const [adjustListing, setAdjustListing] = useState<Listing | null>(null);
  const [adjustMaxDays, setAdjustMaxDays] = useState('');
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);

  // 届出追加モーダル
  const [showAddListing, setShowAddListing] = useState(false);
  const [addForm, setAddForm] = useState({ property_id: '', notification_no: '', license_type: 'housing_business', max_guests: '2', notification_date: '', max_days: '180' });

  // 書類追加モーダル
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [addDocTarget, setAddDocTarget] = useState<string>(''); // listing_id
  const [docForm, setDocForm] = useState({ doc_type: 'periodic_report', doc_name: '', status: 'pending', submitted_at: '', valid_until: '' });

  // 変更届モーダル
  const [showChange, setShowChange] = useState(false);
  const [changeForm, setChangeForm] = useState({ max_guests: '', notification_no: '', change_reason: '' });

  // 届出書類プレビューモーダル（単体 or 全件）
  const [previewListing, setPreviewListing] = useState<Listing | null>(null);
  const [previewAll, setPreviewAll] = useState(false);

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  const listings = data?.listings ?? [];
  const documents = data?.documents ?? [];
  const sorted = [...listings].sort((a, b) => b.days_used - a.days_used);
  const dangerListings = sorted.filter((l) => l.days_used / (l.max_days || 180) > 0.9);
  const warnListings = sorted.filter((l) => { const r = l.days_used / (l.max_days || 180); return r > 0.75 && r <= 0.9; });

  const handleSuspend = async (listing: Listing) => {
    try {
      await api(`/tenant/minpaku/listings/${listing.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'suspended' }) });
      toast(`${listing.property_name} の予約を停止しました`, 'success'); refetch();
    } catch { toast('停止に失敗しました', 'error'); }
  };
  const handleOpenAdjust = (listing: Listing) => { setAdjustListing(listing); setAdjustMaxDays(String(listing.max_days || 180)); };
  const handleSaveAdjust = async () => {
    if (!adjustListing) return;
    try {
      await api(`/tenant/minpaku/listings/${adjustListing.id}`, { method: 'PATCH', body: JSON.stringify({ max_days: Number(adjustMaxDays) }) });
      toast('上限日数を変更しました', 'success'); setAdjustListing(null); refetch();
    } catch { toast('変更に失敗しました', 'error'); }
  };
  const getLocalGov = (address?: string) => { if (!address) return '—'; const m = address.match(/^(.+?[都道府県])(.+?[市区町村])/); return m ? m[2] : '—'; };

  // 届出追加
  const handleAddListing = async () => {
    if (!addForm.property_id || !addForm.notification_no) { toast('物件と届出番号は必須です', 'error'); return; }
    try {
      await api('/tenant/minpaku/listings', {
        method: 'POST', body: JSON.stringify({
          property_id: addForm.property_id, notification_no: addForm.notification_no,
          license_type: addForm.license_type, max_guests: Number(addForm.max_guests),
          max_days: Number(addForm.max_days), notification_date: addForm.notification_date || null, status: 'preparing',
        }),
      });
      toast('届出を追加しました', 'success');
      setShowAddListing(false);
      setAddForm({ property_id: '', notification_no: '', license_type: 'housing_business', max_guests: '2', notification_date: '', max_days: '180' });
      refetch();
    } catch { toast('追加に失敗しました', 'error'); }
  };
  const handleLicenseTypeChange = (v: string) => {
    setAddForm({ ...addForm, license_type: v, max_days: v === 'housing_business' ? '180' : '365' });
  };

  // 書類追加
  const openAddDoc = (listingId: string) => {
    setAddDocTarget(listingId);
    setDocForm({ doc_type: 'periodic_report', doc_name: '', status: 'pending', submitted_at: '', valid_until: '' });
    setShowAddDoc(true);
  };
  const handleAddDoc = async () => {
    const name = docForm.doc_name || docTypeOptions.find((o) => o.value === docForm.doc_type)?.label || docForm.doc_type;
    try {
      await api('/tenant/minpaku/compliance/documents', {
        method: 'POST', body: JSON.stringify({
          listing_id: addDocTarget, doc_type: docForm.doc_type, doc_name: name,
          status: docForm.status, submitted_at: docForm.submitted_at || null, valid_until: docForm.valid_until || null,
        }),
      });
      toast('書類を追加しました', 'success'); setShowAddDoc(false); refetch();
    } catch { toast('追加に失敗しました', 'error'); }
  };

  // 書類ステータス変更
  const handleDocStatusChange = async (docId: string, newStatus: string) => {
    try {
      await api(`/tenant/minpaku/compliance/documents/${docId}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
      toast('ステータスを更新しました', 'success'); refetch();
    } catch { toast('更新に失敗しました', 'error'); }
  };

  // 変更届
  const openChangeForm = () => {
    if (!selectedListing) return;
    setChangeForm({ max_guests: String(selectedListing.max_guests ?? ''), notification_no: selectedListing.notification_no, change_reason: '' });
    setShowChange(true);
  };
  const handleSubmitChange = async () => {
    if (!selectedListing) return;
    try {
      const updates: Record<string, unknown> = {};
      if (changeForm.max_guests && Number(changeForm.max_guests) !== selectedListing.max_guests) updates.max_guests = Number(changeForm.max_guests);
      if (changeForm.notification_no && changeForm.notification_no !== selectedListing.notification_no) updates.notification_no = changeForm.notification_no;
      if (Object.keys(updates).length === 0) { toast('変更項目がありません', 'error'); return; }
      await api(`/tenant/minpaku/listings/${selectedListing.id}`, { method: 'PATCH', body: JSON.stringify(updates) });
      toast('変更届を提出しました', 'success');
      setShowChange(false); setSelectedListing(null); refetch();
    } catch { toast('変更に失敗しました', 'error'); }
  };

  const selectedDocs = selectedListing ? documents.filter((d) => d.notification_no === selectedListing.notification_no) : [];

  return (
    <div className={styles.page}>
      {/* Alert banners */}
      {dangerListings.map((l) => {
        const remaining = (l.max_days || 180) - l.days_used;
        return (
          <div key={`danger-${l.id}`} className={styles.alertRed}>
            <span className={styles.alertIcon}>🚨</span>
            <span className={styles.alertText}><strong>{l.property_name ?? '物件'}</strong> の年間営業日数が{l.days_used}日に達しました。残り{remaining}日で上限に到達します。</span>
            <button className={`${styles.alertBtn} ${styles.alertBtnRed}`} onClick={() => handleSuspend(l)}>予約停止</button>
          </div>
        );
      })}
      {warnListings.map((l) => {
        const remaining = (l.max_days || 180) - l.days_used;
        return (
          <div key={`warn-${l.id}`} className={styles.alertAmber}>
            <span className={styles.alertIcon}>⚠️</span>
            <span className={styles.alertText}><strong>{l.property_name ?? '物件'}</strong> の年間営業日数が{l.days_used}日に達しました。残り{remaining}日。</span>
            <button className={`${styles.alertBtn} ${styles.alertBtnAmber}`} onClick={() => handleOpenAdjust(l)}>調整設定</button>
          </div>
        );
      })}

      <Card title="📜 住宅宿泊事業者届出一覧" actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={styles.btn} onClick={() => setPreviewAll(true)}>📄 届出書類</button>
          <button className={styles.btnPrimary} onClick={() => setShowAddListing(true)}>＋ 届出追加</button>
        </div>
      }>
        <table className={styles.table}>
          <thead><tr>
            <th className={styles.th}>物件名</th><th className={styles.th}>届出番号</th><th className={styles.th}>届出自治体</th>
            <th className={styles.th}>届出日</th><th className={styles.th}>年間営業日</th><th className={styles.th}>残日数</th><th className={styles.th}>ステータス</th>
          </tr></thead>
          <tbody>
            {sorted.map((l) => {
              const maxDays = l.max_days || 180; const pct = (l.days_used / maxDays) * 100; const remaining = maxDays - l.days_used;
              const barClass = pct > 90 ? styles.barFillRed : pct > 75 ? styles.barFillAmber : styles.barFillGreen;
              const statusClass = pct > 90 ? styles.statusDanger : pct > 75 ? styles.statusWarn : styles.statusOk;
              const statusText = pct > 90 ? '緊急停止推奨' : pct > 75 ? '要注意' : '正常';
              return (
                <tr key={l.id} className={styles.clickRow} onClick={() => setSelectedListing(l)}>
                  <td className={styles.td}><div style={{ fontWeight: 600 }}>{l.property_name ?? '—'}</div>{l.property_address && <div className={styles.tdSub}>{l.property_address}</div>}</td>
                  <td className={styles.td}><code className={styles.monoSmall}>{l.notification_no || '—'}</code></td>
                  <td className={styles.td}>{getLocalGov(l.property_address)}</td>
                  <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>{l.notification_date ? new Date(l.notification_date).toLocaleDateString('ja-JP') : '—'}</td>
                  <td className={styles.td}><div className={styles.barMini}><div className={styles.barTrack}><div className={`${styles.barFill} ${barClass}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div><span className={styles.daysText}>{l.days_used}/{maxDays}</span></div></td>
                  <td className={styles.td}><span className={`${styles.remainText} ${remaining <= 20 ? styles.remainDanger : remaining <= 40 ? styles.remainWarn : ''}`}>残{remaining}日</span></td>
                  <td className={styles.td}><span className={`${styles.statusBadge} ${statusClass}`}>{statusText}</span></td>
                </tr>
              );
            })}
            {listings.length === 0 && <tr><td className={styles.td} colSpan={7} style={{ textAlign: 'center', color: 'var(--ink3)' }}>届出データなし</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title="📚 住宅宿泊事業法 主要規制">
        <div className={styles.regGrid}>
          {regulations.map((r) => (
            <div key={r.title} className={styles.regCard}>
              <span className={styles.regIcon}>{r.icon}</span>
              <span className={styles.regTitle} style={{ color: r.color }}>{r.title}</span>
              <span className={styles.regDesc}>{r.desc}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* ===== 届出詳細スライドパネル ===== */}
      <SlidePanel open={!!selectedListing} onClose={() => setSelectedListing(null)} title={`届出詳細 — ${selectedListing?.property_name ?? ''}`} actions={
        <>
          <button className={styles.btn} onClick={() => { if (selectedListing) setPreviewListing(selectedListing); }}>📄 届出書類</button>
          <button className={styles.btnPrimary} onClick={openChangeForm}>✏️ 変更届</button>
        </>
      }>
        {selectedListing && (
          <>
            <PanelSection title="届出情報">
              <SpecGrid>
                <SpecCell label="届出番号" value={selectedListing.notification_no || '—'} mono />
                <SpecCell label="届出自治体" value={getLocalGov(selectedListing.property_address)} />
                <SpecCell label="届出日" value={selectedListing.notification_date ? new Date(selectedListing.notification_date).toLocaleDateString('ja-JP') : '—'} mono />
                <SpecCell label="住宅所在地" value={selectedListing.property_address || '—'} />
                <SpecCell label="最大宿泊人数" value={`${selectedListing.max_guests ?? '—'}名`} />
                <SpecCell label="免許種別" value={selectedListing.license_type === 'housing_business' ? '住宅宿泊事業' : '特区民泊'} />
              </SpecGrid>
            </PanelSection>
            <PanelSection title="年間営業日数">
              <div className={styles.daysRingWrap}>
                <div className={styles.daysRing}>
                  <svg viewBox="0 0 52 52" width="72" height="72">
                    <circle cx="26" cy="26" r="22" fill="none" stroke="var(--surface3)" strokeWidth="5" />
                    <circle cx="26" cy="26" r="22" fill="none"
                      stroke={selectedListing.days_used / (selectedListing.max_days || 180) > 0.9 ? 'var(--red)' : selectedListing.days_used / (selectedListing.max_days || 180) > 0.75 ? 'var(--amber)' : 'var(--green)'}
                      strokeWidth="5" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 22}`}
                      strokeDashoffset={`${2 * Math.PI * 22 * (1 - selectedListing.days_used / (selectedListing.max_days || 180))}`}
                      transform="rotate(-90 26 26)" />
                  </svg>
                  <div className={styles.daysRingCenter}><span className={styles.daysRingNum}>{selectedListing.days_used}</span><span className={styles.daysRingUnit}>日</span></div>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 5 }}>{selectedListing.days_used}日 <span style={{ fontSize: 13, color: 'var(--ink3)', fontWeight: 400 }}>/ {selectedListing.max_days || 180}日上限</span></div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: (selectedListing.max_days || 180) - selectedListing.days_used <= 20 ? 'var(--red)' : 'var(--amber)' }}>残り {(selectedListing.max_days || 180) - selectedListing.days_used}日</div>
                </div>
              </div>
            </PanelSection>
            <PanelSection title="関係書類">
              {selectedDocs.length > 0 ? (
                <div className={styles.panelDocList}>
                  {selectedDocs.map((d) => (
                    <div key={d.id} className={styles.panelDocItem}>
                      <span className={styles.panelDocIcon}>{d.doc_type === 'fire_certificate' ? '📋' : d.doc_type === 'insurance' ? '🛡️' : '📄'}</span>
                      <div className={styles.panelDocInfo}>
                        <div className={styles.panelDocName}>{d.doc_name || docTypeLabel[d.doc_type] || d.doc_type}</div>
                        <div className={styles.panelDocMeta}>{d.submitted_at ? `${new Date(d.submitted_at).toLocaleDateString('ja-JP')} 提出済` : '未提出'}</div>
                      </div>
                      <select className={styles.statusSelect} value={d.status} onChange={(e) => handleDocStatusChange(d.id, e.target.value)}>
                        <option value="pending">未提出</option><option value="submitted">提出済</option><option value="valid">有効</option>
                      </select>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: 12, color: 'var(--ink3)', fontSize: 13 }}>関連書類なし</div>
              )}
              <button className={styles.addDocBtn} onClick={() => { if (selectedListing) openAddDoc(selectedListing.id); }}>＋ 書類追加</button>
            </PanelSection>
          </>
        )}
      </SlidePanel>

      {/* ===== 届出書類プレビューモーダル（単体 or 全件） ===== */}
      {(!!previewListing || previewAll) && (
        <div className={styles.printOverlay}>
          <div className={styles.printPanel} data-print-content>
            <div className={styles.printToolbar} data-print-hide>
              <button className={styles.btn} onClick={() => { setPreviewListing(null); setPreviewAll(false); }}>✕ 閉じる</button>
              <button className={styles.btnPrimary} onClick={() => {
                const content = document.querySelector('[data-print-content] > :last-child');
                if (!content) return;
                const css = `body{font-family:'Noto Sans JP',sans-serif;padding:30px;color:#333;font-size:13px}table{width:100%;border-collapse:collapse;margin-bottom:4px}th{text-align:left;padding:6px 10px;font-weight:600;color:#555;width:140px;border-bottom:1px solid #ddd;background:#f5f5f2}td{padding:6px 10px;border-bottom:1px solid #ddd}.header{font-size:16px;font-weight:800;text-align:center;padding:8px 0 14px;border-bottom:2px solid #333;margin-bottom:14px}.sub-header{font-size:13px;font-weight:700;margin:18px 0 8px;padding-bottom:4px;border-bottom:1px solid #ddd}.footer{margin-top:20px;font-size:11px;color:#999;text-align:right}.mono{font-family:monospace;font-size:12px}hr{border:none;border-top:2px dashed #ddd;margin:24px 0}@media print{body{padding:10px}hr{page-break-before:always;border:none;margin:0}}`;
                let iframe = document.getElementById('print-iframe') as HTMLIFrameElement;
                if (!iframe) { iframe = document.createElement('iframe'); iframe.id = 'print-iframe'; iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:800px;height:600px'; document.body.appendChild(iframe); }
                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (!doc) return;
                doc.open();
                doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>届出書類</title><style>${css}</style></head><body>${content.innerHTML}</body></html>`);
                doc.close();
                setTimeout(() => { iframe.contentWindow?.print(); }, 300);
              }}>📥 PDF保存</button>
            </div>
            <div className={styles.previewBody}>
              {(previewAll ? sorted : previewListing ? [previewListing] : []).map((listing, idx) => {
                const pDocs = documents.filter((d) => d.notification_no === listing.notification_no);
                const maxDays = listing.max_days || 180;
                const remaining = maxDays - listing.days_used;
                return (
                  <div key={listing.id} className={styles.previewSection}>
                    {idx > 0 && <hr className={styles.previewDivider} data-divider />}
                    <div className={`${styles.previewHeader} header`}>住宅宿泊事業 届出情報</div>
                    <table className={styles.previewTable}>
                      <tbody>
                        <tr><th>物件名</th><td>{listing.property_name}</td></tr>
                        <tr><th>届出番号</th><td className={`${styles.mono} mono`}>{listing.notification_no}</td></tr>
                        <tr><th>届出自治体</th><td>{getLocalGov(listing.property_address)}</td></tr>
                        <tr><th>届出日</th><td className={`${styles.mono} mono`}>{listing.notification_date ? new Date(listing.notification_date).toLocaleDateString('ja-JP') : '—'}</td></tr>
                        <tr><th>住宅所在地</th><td>{listing.property_address || '—'}</td></tr>
                        <tr><th>免許種別</th><td>{listing.license_type === 'housing_business' ? '住宅宿泊事業' : '特区民泊'}</td></tr>
                        <tr><th>最大宿泊人数</th><td>{listing.max_guests ?? '—'}名</td></tr>
                        <tr><th>年間上限日数</th><td>{maxDays}日</td></tr>
                        <tr><th>現在使用日数</th><td><strong>{listing.days_used}日</strong>（残り{remaining}日）</td></tr>
                      </tbody>
                    </table>
                    {pDocs.length > 0 && (
                      <>
                        <div className={`${styles.previewSubHeader} sub-header`}>関連書類一覧</div>
                        <table className={styles.previewTable}>
                          <thead><tr><th>書類名</th><th>ステータス</th><th>提出日</th><th>有効期限</th></tr></thead>
                          <tbody>
                            {pDocs.map((d) => (
                              <tr key={d.id}>
                                <td>{d.doc_name || docTypeLabel[d.doc_type] || d.doc_type}</td>
                                <td>{docStatusLabel[d.status] ?? d.status}</td>
                                <td className={`${styles.mono} mono`}>{d.submitted_at ? new Date(d.submitted_at).toLocaleDateString('ja-JP') : '—'}</td>
                                <td className={`${styles.mono} mono`}>{d.valid_until ? new Date(d.valid_until).toLocaleDateString('ja-JP') : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                );
              })}
              <div className={`${styles.previewFooter} footer`}>出力日: {new Date().toLocaleDateString('ja-JP')}</div>
            </div>
          </div>
        </div>
      )}

      {/* ===== 届出書類一覧モーダル（コンプライアンス書類一覧） ===== */}
      <Modal open={showDocs} onClose={() => setShowDocs(false)} title="📋 コンプライアンス書類" width={640}>
        {documents.length > 0 ? (
          <div className={styles.docList}>
            {documents.map((d) => (
              <div key={d.id} className={styles.docItem}>
                <div className={styles.docInfo}>
                  <div className={styles.docName}>{d.doc_name || docTypeLabel[d.doc_type] || d.doc_type}</div>
                  <div className={styles.docMeta}>{d.property_name} {d.notification_no && `| ${d.notification_no}`}{d.valid_until && ` | 有効期限: ${new Date(d.valid_until).toLocaleDateString('ja-JP')}`}</div>
                </div>
                <Badge variant={docStatusVariant[d.status] ?? 'gray'}>{docStatusLabel[d.status] ?? d.status}</Badge>
              </div>
            ))}
          </div>
        ) : <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>書類データなし</div>}
      </Modal>

      {/* ===== 届出追加モーダル ===== */}
      <Modal open={showAddListing} onClose={() => setShowAddListing(false)} title="＋ 新規届出追加" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><button className={styles.btn} onClick={() => setShowAddListing(false)}>キャンセル</button><button className={styles.btnPrimary} onClick={handleAddListing}>追加</button></div>
      }>
        <div className={styles.modalForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>物件 *</label>
            <select className={styles.formInput} value={addForm.property_id} onChange={(e) => setAddForm({ ...addForm, property_id: e.target.value })}>
              <option value="">選択してください</option>
              {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>届出番号 *</label>
            <input className={styles.formInput} value={addForm.notification_no} onChange={(e) => setAddForm({ ...addForm, notification_no: e.target.value })} placeholder="M131234570" />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>免許種別</label>
              <select className={styles.formInput} value={addForm.license_type} onChange={(e) => handleLicenseTypeChange(e.target.value)}>
                <option value="housing_business">住宅宿泊事業（180日）</option>
                <option value="special_zone">特区民泊（365日）</option>
              </select>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>最大宿泊人数</label>
              <input className={styles.formInput} type="number" value={addForm.max_guests} onChange={(e) => setAddForm({ ...addForm, max_guests: e.target.value })} min="1" />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>届出日</label>
              <input className={styles.formInput} type="date" value={addForm.notification_date} onChange={(e) => setAddForm({ ...addForm, notification_date: e.target.value })} />
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>年間上限日数</label>
              <input className={styles.formInput} type="number" value={addForm.max_days} onChange={(e) => setAddForm({ ...addForm, max_days: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>

      {/* ===== 書類追加モーダル ===== */}
      <Modal open={showAddDoc} onClose={() => setShowAddDoc(false)} title="📄 書類追加" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><button className={styles.btn} onClick={() => setShowAddDoc(false)}>キャンセル</button><button className={styles.btnPrimary} onClick={handleAddDoc}>追加</button></div>
      }>
        <div className={styles.modalForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>書類種別</label>
            <select className={styles.formInput} value={docForm.doc_type} onChange={(e) => setDocForm({ ...docForm, doc_type: e.target.value, doc_name: '' })}>
              {docTypeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>書類名</label>
            <input className={styles.formInput} value={docForm.doc_name} onChange={(e) => setDocForm({ ...docForm, doc_name: e.target.value })} placeholder="空欄の場合は種別名が使用されます" />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>提出日</label>
              <input className={styles.formInput} type="date" value={docForm.submitted_at} onChange={(e) => setDocForm({ ...docForm, submitted_at: e.target.value })} />
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>有効期限</label>
              <input className={styles.formInput} type="date" value={docForm.valid_until} onChange={(e) => setDocForm({ ...docForm, valid_until: e.target.value })} />
            </div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>ステータス</label>
            <select className={styles.formInput} value={docForm.status} onChange={(e) => setDocForm({ ...docForm, status: e.target.value })}>
              <option value="pending">未提出</option><option value="submitted">提出済</option><option value="valid">有効</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* ===== 変更届モーダル ===== */}
      <Modal open={showChange} onClose={() => setShowChange(false)} title="✏️ 変更届" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><button className={styles.btn} onClick={() => setShowChange(false)}>キャンセル</button><button className={styles.btnPrimary} onClick={handleSubmitChange}>変更届を提出</button></div>
      }>
        {selectedListing && (
          <div className={styles.modalForm}>
            <div className={styles.adjustInfo}>
              <strong>{selectedListing.property_name}</strong> — {selectedListing.notification_no}
              <br />{selectedListing.property_address}
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}><label className={styles.formLabel}>届出番号</label>
                <input className={styles.formInput} value={changeForm.notification_no} onChange={(e) => setChangeForm({ ...changeForm, notification_no: e.target.value })} />
              </div>
              <div className={styles.formGroup}><label className={styles.formLabel}>最大宿泊人数</label>
                <input className={styles.formInput} type="number" value={changeForm.max_guests} onChange={(e) => setChangeForm({ ...changeForm, max_guests: e.target.value })} min="1" />
              </div>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>変更理由</label>
              <textarea className={styles.formTextarea} value={changeForm.change_reason} onChange={(e) => setChangeForm({ ...changeForm, change_reason: e.target.value })} placeholder="変更理由を記載してください" rows={3} />
            </div>
          </div>
        )}
      </Modal>

      {/* ===== 営業日数上限調整モーダル ===== */}
      <Modal open={!!adjustListing} onClose={() => setAdjustListing(null)} title="営業日数上限調整" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><button className={styles.btn} onClick={() => setAdjustListing(null)}>キャンセル</button><button className={styles.btnPrimary} onClick={handleSaveAdjust}>保存</button></div>
      }>
        {adjustListing && (
          <div className={styles.adjustForm}>
            <div className={styles.adjustInfo}><strong>{adjustListing.property_name}</strong><br />現在: {adjustListing.days_used}日使用 / {adjustListing.max_days || 180}日上限</div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>年間上限日数</label>
              <input className={styles.formInput} type="number" value={adjustMaxDays} onChange={e => setAdjustMaxDays(e.target.value)} min="1" max="365" />
              <span className={styles.formHint}>住宅宿泊事業: 最大180日 / 特区民泊: 最大365日</span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
